import { ConfigProvider, message } from 'antd';
import { FC, useMemo, useState } from 'react';

import { AccessDenied } from '@entities';
import {
  AccessMapDrawer,
  AccessMapTable,
  AccessMapToolbar,
  exportAccessMapCsv,
  filterMemberships,
  groupByCompany,
  groupByEmployee,
} from '@entities/AccessMap';
import type {
  AccessMapFilters,
  AccessMapTarget,
  AccessMapView,
  CompanyGroup,
  EmployeeGroup,
} from '@entities/AccessMap';
import { useAccount } from '@hooks';
import { useAccessMap } from '@hooks-api';
import { lessRoundedTags } from '@theme';

import * as S from './styled';

const CompanyAccessMap: FC = () => {
  const { role } = useAccount();
  const { memberships, isLoading } = useAccessMap();

  const [view, setView] = useState<AccessMapView>('company');
  const [filters, setFilters] = useState<AccessMapFilters>({ term: '' });
  const [target, setTarget] = useState<AccessMapTarget | null>(null);

  const filtered = useMemo(
    () => filterMemberships(memberships, filters),
    [memberships, filters]
  );
  const employeeGroups = useMemo(
    () => groupByEmployee(filtered),
    [filtered]
  );
  const companyGroups = useMemo(() => groupByCompany(filtered), [filtered]);

  // Defense in depth: the router guards on the parent route's roles, not this
  // matched route's, so the page re-checks the admin role itself.
  if (role !== 'admin') return <AccessDenied />;

  const handleExport = () => {
    if (!filtered.length) return;
    exportAccessMapCsv(filtered);
    message.success('Access map exported');
  };

  const handleSelectEmployee = (group: EmployeeGroup) =>
    setTarget({ kind: 'employee', group });
  const handleSelectCompany = (group: CompanyGroup) =>
    setTarget({ kind: 'company', group });

  return (
    <ConfigProvider theme={lessRoundedTags}>
      <S.Wrapper gap={24} vertical>
        <S.Header vertical gap={2}>
          <S.Title>Access map</S.Title>
          <S.Subtitle>
            Which team members can access each client company’s portal
          </S.Subtitle>
        </S.Header>

        <AccessMapToolbar
          view={view}
          filters={filters}
          exportDisabled={!filtered.length}
          onViewChange={setView}
          onFiltersChange={setFilters}
          onExport={handleExport}
        />

        <AccessMapTable
          view={view}
          employeeGroups={employeeGroups}
          companyGroups={companyGroups}
          isLoading={isLoading}
          onSelectEmployee={handleSelectEmployee}
          onSelectCompany={handleSelectCompany}
        />

        <AccessMapDrawer
          open={!!target}
          target={target}
          onClose={() => setTarget(null)}
        />
      </S.Wrapper>
    </ConfigProvider>
  );
};

export default CompanyAccessMap;
